/**
 * Shared / marketplace datasets — ROOT access toggle (Phase 8, P7A UI).
 *
 * Lists the shared data products (e.g. `plentiful`) and, per dataset, the
 * per-tenant read grants. ROOT flips a tenant on/off; the server (owner-guard,
 * audit, cache-invalidate + L3 rebuild) re-checks and applies every toggle, and
 * the change takes effect on the subscriber's next request. Gated
 * PLATFORM_DATASET_GRANT, hidden for non-ROOT (see AuthedRoutes).
 */

import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, Chip, Alert, Tooltip, IconButton, TextField, Button, Grid, Divider,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DatasetIcon from '@mui/icons-material/Dataset';
import swal from 'sweetalert';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import {
  listSharedDatasets, listDatasetGrants, toggleDatasetGrant, listAllTenants,
} from '../../../utilityFunctions/PlatformAdminUtil';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

export default function SharedDatasetsScreen() {
  const [datasets, setDatasets] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [grants, setGrants] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [grantsLoading, setGrantsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [newTenantId, setNewTenantId] = React.useState('');
  const [tenants, setTenants] = React.useState([]);

  const loadDatasets = React.useCallback(async () => {
    setLoading(true);
    const res = await listSharedDatasets();
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    setError(null);
    const list = Array.isArray(res.data) ? res.data : [];
    setDatasets(list);
    setSelected((prev) => prev || (list.length > 0 ? list[0] : null));
  }, []);

  const loadGrants = React.useCallback(async (datasetId) => {
    if (!datasetId) return;
    setGrantsLoading(true);
    const res = await listDatasetGrants(datasetId);
    setGrantsLoading(false);
    setGrants(res.ok && Array.isArray(res.data) ? res.data : []);
  }, []);

  const loadTenants = React.useCallback(async () => {
    const res = await listAllTenants();
    setTenants(res.ok && Array.isArray(res.data) ? res.data : []);
  }, []);

  React.useEffect(() => { loadDatasets(); }, [loadDatasets]);
  React.useEffect(() => { loadTenants(); }, [loadTenants]);
  React.useEffect(() => { if (selected) loadGrants(selected.id); }, [selected, loadGrants]);

  const applyToggle = async (tenantId, enabled) => {
    if (!selected) return;
    const res = await toggleDatasetGrant(selected.id, tenantId, enabled);
    if (!res.ok) {
      swal({ title: 'Update failed', text: res.error || 'Could not update grant', icon: 'error' });
      return;
    }
    await loadGrants(selected.id);
  };

  const handleGrantNew = async () => {
    const tenantId = parseInt(newTenantId, 10);
    if (!tenantId || Number.isNaN(tenantId)) {
      swal({ title: 'Enter a tenant id', icon: 'info' });
      return;
    }
    setNewTenantId('');
    await applyToggle(tenantId, true);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', mt: '5rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <DatasetIcon sx={{ color: Colors.primary }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: Colors.primary }}>Shared datasets</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Grant or revoke a tenant's read-only access to a shared data product. Changes are audited and
        take effect on the subscriber's next request.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Dataset list */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Data products</Typography>
              <Tooltip title="Reload"><span>
                <IconButton size="small" onClick={loadDatasets} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
              </span></Tooltip>
            </Box>
            <Divider />
            {loading && <CustomLoader />}
            {!loading && datasets.length === 0 && (
              <Alert severity="info" sx={{ m: 1.5 }}>No shared datasets configured.</Alert>
            )}
            {!loading && datasets.map((ds) => (
              <Box
                key={ds.id}
                onClick={() => setSelected(ds)}
                sx={{
                  px: 1.5, py: 1.25, cursor: 'pointer',
                  borderLeft: selected && selected.id === ds.id ? `3px solid ${Colors.primary}` : '3px solid transparent',
                  backgroundColor: selected && selected.id === ds.id ? `${Colors.primary}10` : 'transparent',
                  '&:hover': { backgroundColor: `${Colors.primary}08` },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{ds.displayName || ds.catalog}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                  {ds.catalog} · owner #{ds.ownerTenantId}
                </Typography>
                {ds.status && ds.status !== 'ACTIVE' && (
                  <Chip size="small" label={ds.status} sx={{ ml: 1 }} />
                )}
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Grants for selected dataset */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
            {!selected && <Alert severity="info">Select a dataset to manage its tenant grants.</Alert>}
            {selected && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Grants · <span style={{ fontFamily: 'monospace' }}>{selected.catalog}</span>
                  </Typography>
                  <Tooltip title="Reload"><span>
                    <IconButton size="small" onClick={() => loadGrants(selected.id)} disabled={grantsLoading}><RefreshIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                </Box>

                {/* Grant a new tenant */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                  {tenants.length > 0 ? (
                    <FormControl size="small" sx={{ width: 260 }}>
                      <InputLabel id="grant-tenant-select-label">Tenant</InputLabel>
                      <Select
                        labelId="grant-tenant-select-label"
                        label="Tenant"
                        value={newTenantId}
                        onChange={(e) => setNewTenantId(e.target.value)}
                      >
                        {tenants.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{`${t.displayName || t.slug} (#${t.id})`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      size="small" label="Grant tenant id" value={newTenantId}
                      onChange={(e) => setNewTenantId(e.target.value.replace(/[^0-9]/g, ''))}
                      sx={{ width: 180 }}
                      helperText="Numeric tenant ID (database id)"
                    />
                  )}
                  <Button variant="contained" onClick={handleGrantNew}
                    sx={{ backgroundColor: Colors.primary }}>Grant access</Button>
                </Box>

                {grantsLoading && <CustomLoader />}
                {!grantsLoading && grants.length === 0 && (
                  <Alert severity="info">No tenant grants yet for this dataset.</Alert>
                )}
                {!grantsLoading && grants.length > 0 && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 620, '& th': { fontWeight: 700, color: 'text.secondary' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Tenant</TableCell>
                          <TableCell>Access</TableCell>
                          <TableCell>Granted by</TableCell>
                          <TableCell>Granted at</TableCell>
                          <TableCell>Revoked at</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {grants.map((g) => (
                          <TableRow key={g.id != null ? g.id : g.tenantId}>
                            <TableCell sx={{ fontWeight: 600 }}>#{g.tenantId}</TableCell>
                            <TableCell>
                              <Switch
                                checked={!!g.enabled}
                                onChange={(e) => applyToggle(g.tenantId, e.target.checked)}
                              />
                              <Chip size="small" label={g.enabled ? 'Enabled' : 'Revoked'}
                                color={g.enabled ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell>{g.grantedBy != null ? `#${g.grantedBy}` : '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(g.grantedAt)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(g.revokedAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
